import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { toast } from 'src/hooks/UseToast';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';
import {
    simulateDisplayRequestFormSchema,
    SimulateDisplayRequestFormSchemaValues,
    useSimulateDisplayRequest,
} from 'src/features/actions/api/useSimulateDisplayRequest';
import { actionsMessages } from 'src/features/actions/utils/messages';
import SimulateDisplayRequestForm from 'src/features/actions/components/form/SimulateDisplayRequestForm';
import { useParking } from 'src/providers/ParkingProvider';

type SimulateDisplayRequestFormModalProps = {
    isModalOpen: boolean;
    setIsModalOpen: (isOpen: boolean) => void;
};
const SimulateDisplayRequestFormModal = ({ isModalOpen, setIsModalOpen }: SimulateDisplayRequestFormModalProps) => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const form = useForm<SimulateDisplayRequestFormSchemaValues>({
        resolver: zodResolver(simulateDisplayRequestFormSchema),
        defaultValues: {},
    });

    const onError = useOnError();

    const { mutate, isPending } = useSimulateDisplayRequest({
        onSuccess: () => {
            setIsModalOpen(false);
            toast({
                title: formatMessage(actionsMessages.createdDisplayMessage),
                description: formatMessage(actionsMessages.createdDisplayMessageDescription),
            });
        },
        onError: (error) => {
            onError(error, form.setError);
        },
    });

    const onSubmit = React.useCallback(
        (values: SimulateDisplayRequestFormSchemaValues) => {
            const { componentId, setting_json_placeholders, ...rest } = values;

            const filteredSettingsPlaceholders =
                setting_json_placeholders &&
                Object.fromEntries(Object.entries(setting_json_placeholders).filter(([_, v]) => v != null));

            mutate({
                parkingId,
                componentId,
                setting_json_placeholders: filteredSettingsPlaceholders,
                ...rest,
            });
        },

        [mutate, parkingId],
    );

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
            form.reset();
        }
        setIsModalOpen(isOpen);
        if (isOpen) {
            form.reset();
        }
    };

    return (
        <Dialog open={isModalOpen} onOpenChange={onOpenChange} modal>
            <DialogContent>
                <DialogHeader>
                    <div className="flex gap-4">
                        <SquareIcon icon={ICON_MAP.MESSAGE} />
                        <div>
                            <DialogTitle>{formatMessage(actionsMessages.displayMessage)}</DialogTitle>
                            <DialogDescription>{formatMessage(actionsMessages.sendMessageToDisplay)}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <DialogSeparator />

                <DialogBody>
                    <SimulateDisplayRequestForm form={form} onSubmit={form.handleSubmit(onSubmit)} />
                </DialogBody>
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button variant="default" type="submit" onClick={form.handleSubmit(onSubmit)} className="flex-1">
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {formatMessage(actionsMessages.sendMessage)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default SimulateDisplayRequestFormModal;
